'use client';

import * as React from 'react';

import { cn } from './cn';
import { CONTROL_TEXT_CLASS, controlSizeVariants } from './control-size';
import { useFieldControl } from './field';

/**
 * The new-system text input — Phase 2 (MR-1) of the MUI → Tailwind migration.
 *
 * Structure: a wrapper `div` owns the visible control chrome (border, fill,
 * shadow, focus ring, invalid/disabled treatments) and an `<input>` fills it.
 * That mirrors the DOM existing scoped field rules address: the bounded
 * legacy-spelled class hooks paint the real control border, never an inner
 * no-op. They are owned DOM vocabulary, not a framework or style interpreter.
 * `className`/`style` address the wrapper; `inputClassName`/`inputStyle` the
 * `<input>` itself. Keep those finite hooks while their scoped readers exist.
 * Native input-start/input-end adornment slots mirror Textarea's slot shape:
 * per-owner ink rules can target the actual wrappers without a vendor
 * adornment component or an extra styling engine.
 *
 * Colours come from the token bridge only; states are DESIGN-SPEC §3.3:
 * default = border-input/bg-card/shadow-xs, hover = parity (no change — the
 * theme pins hover to the divider), focus-visible = the house 2px ring + 2px
 * offset with border-ring, invalid = destructive-text border, disabled = the
 * action-disabled family, readOnly = bg-muted. The 33.75/36/40.5px height
 * scale comes from the control scale against the display-size root font, so
 * the three display sizes hold — and BOTH the height and the type size come
 * from `ui/control-size`, so this control cannot drift from the other field
 * atoms. See that module for the `--minerva-control-h` /
 * `FormStack controlSize="comfortable"` contract; an explicit `h-10` on a
 * control still wins (tailwind-merge).
 *
 * Inside a `Field` the control inherits the auto-wiring: the `id` defaults to
 * the Field's, `aria-describedby`/`aria-invalid` come from its context — the
 * Message slot id and invalid flag — unless the caller overrides them.
 */

// Shared with ui/textarea: the state treatments both chrome wrappers declare.
export const inputWrapperStateClasses = {
  focus:
    'has-[:focus-visible]:[outline:2px_solid_var(--color-ring)] has-[:focus-visible]:outline-offset-2',
  invalid: 'border-[color:var(--color-destructive-text)]',
  disabled:
    'border-[var(--minerva-palette-action-disabled)] bg-[var(--minerva-palette-action-disabledBackground)] shadow-none',
  /*
   * READ-ONLY, KEYED ON THIS COMPONENT'S OWN PROP — not on `:read-only`.
   *
   * `has-[:read-only]:bg-muted` was doing what it literally says rather than
   * what it means: `:read-only` matches ANY non-editable element, so an
   * adornment `<div>` inside the wrapper satisfied it and an input the user
   * could type into rendered with the read-only fill — the same `#F5F7F8`
   * (`--minerva-surface-muted`) a genuinely read-only control carries. Every
   * geometric check passed it; the per-probe FRAMES found it, which is what
   * they are for (design review round 2 on !1474, D1).
   *
   * The wrapper now carries `data-readonly` when `readOnly` is set on the
   * control itself, so only this component's own state can turn the treatment
   * on and no descendant can imply it.
   */
  readOnly: 'data-[readonly=true]:bg-muted',
} as const;

export interface UiInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'className' | 'size' | 'style'
  > {
  /** Wrapper (the visible control) classes. */
  className?: string;
  /** Wrapper style. */
  style?: React.CSSProperties;
  /** The `<input>` element itself. */
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  /** Explicit override; otherwise from the Field context. */
  invalid?: boolean;
  /**
   * Content inside the control chrome, before / after the `<input>` (an
   * icon, a unit, a mode selector). The slot supplies the 12px edge inset
   * the `<input>`'s own `px-3` gives text, so an adornment sits where the
   * text would have; its own inner spacing is the caller's.
   */
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, UiInputProps>(
  function Input(
    {
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      className,
      disabled,
      id,
      inputClassName,
      inputStyle,
      invalid,
      onBlur,
      onFocus,
      readOnly,
      required,
      startAdornment,
      endAdornment,
      style,
      ...rest
    },
    ref
  ) {
    const field = useFieldControl();
    const [focused, setFocused] = React.useState(false);

    const resolvedId = id ?? field.id;
    const resolvedInvalid = invalid ?? field.invalid ?? false;
    const describedBy = ariaDescribedBy ?? field.describedBy;
    const resolvedRequired = required ?? field.required;
    // MUI FormControl parity: a disabled Field propagates to its control.
    const resolvedDisabled = disabled ?? field.disabled ?? false;
    // The read-only treatment is keyed to this control's own prop, so an
    // adornment (or any other non-editable descendant) cannot imply it.
    const resolvedReadOnly = readOnly ?? false;

    const stateHookClasses = cn(
      'MuiInputBase-root',
      // MUI applies the state classes to the wrapper root (its OutlinedInput
      // root is what call sites read), so the hooks ride the wrapper here too.
      focused && 'Mui-focused',
      resolvedInvalid && 'Mui-error',
      resolvedDisabled && 'Mui-disabled'
    );

    return (
      <div
        className={cn(
          // `shrink-0`: a height-constrained field root must never squash the
          // control (an 18px click target is the measured failure mode —
          // design-qa caught it when a call site sized the root instead of
          // the control); mixed-row heights belong on this wrapper. See
          // ui/field.tsx "sizing" note.
          // The height AND the type size come from the shared contract, so the
          // wrapper's declared scale is the same object the Select trigger
          // reads (ui/control-size). `text-sm` on the wrapper is not redundant
          // with the `<input>`'s own: it is what an adornment or a placeholder
          // slot inherits, and it is the number the cross-atom regression
          // assertion compares.
          controlSizeVariants({ size: 'md' }),
          'minerva-input relative flex w-full min-w-0 shrink-0 items-center rounded-lg border border-input bg-card shadow-xs transition-colors duration-150 ease-out',
          'MuiOutlinedInput-root',
          // The focus border keeps the legacy primary-border continuity; when
          // invalid, the destructive border stays (the ring is the focus cue).
          !resolvedInvalid && 'has-[:focus-visible]:border-ring',
          inputWrapperStateClasses.focus,
          resolvedInvalid && inputWrapperStateClasses.invalid,
          resolvedDisabled && inputWrapperStateClasses.disabled,
          !resolvedDisabled &&
            resolvedReadOnly &&
            inputWrapperStateClasses.readOnly,
          stateHookClasses,
          className
        )}
        data-readonly={
          !resolvedDisabled && resolvedReadOnly ? 'true' : undefined
        }
        style={style}
      >
        {startAdornment ? (
          <div
            data-slot="input-start-adornment"
            className="flex shrink-0 items-center self-stretch pl-3"
          >
            {startAdornment}
          </div>
        ) : null}
        <input
          ref={ref}
          aria-describedby={describedBy}
          aria-invalid={resolvedInvalid || undefined}
          className={cn(
            CONTROL_TEXT_CLASS,
            'autofill-fix MuiInputBase-input MuiOutlinedInput-input h-full w-full min-w-0 flex-1 bg-transparent px-3 text-foreground leading-5 outline-none',
            'placeholder:text-muted-foreground',
            /*
             * The disabled cursor is NOT declared here: `input:disabled` is in
             * the base-layer cursor contract in `src/tailwind.css`, which is the
             * one place every atom's pointer affordance comes from. An
             * agreeing utility here is a second statement of it, and the two
             * drift apart the first time one of them changes.
             */
            'disabled:text-[var(--minerva-palette-text-disabled)]',
            inputClassName
          )}
          disabled={resolvedDisabled}
          id={resolvedId}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          required={resolvedRequired}
          readOnly={resolvedReadOnly}
          style={inputStyle}
          {...rest}
        />
        {endAdornment ? (
          <div
            data-slot="input-end-adornment"
            className="flex shrink-0 items-center self-stretch pr-3"
          >
            {endAdornment}
          </div>
        ) : null}
      </div>
    );
  }
);
Input.displayName = 'Input';
